import React, { useEffect, useState } from "react";
import { createProfile, getProfile } from "../api/profileService";
import { useNavigate } from "react-router";
import toast from "react-hot-toast";
import { enrollNow } from "../api/homeService";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import UserProfile from "../Assets/images/userProfile.webp";

const PersonalInfoPage = ({
  profilePageInfo,
  isLoginModalOpen,
  setIsLoginModalOpen,
  isEnroll,
  course,
}) => {
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [disableEmail, setDisableEmail] = useState(false);
  const [disablePhone, setDisablePhone] = useState(false);
  const navigate = useNavigate();

  // Consent state
  const [acceptConsent, setAcceptConsent] = useState(false);
  const [linksOpened, setLinksOpened] = useState({
    terms: false,
    privacy: false,
    refund: false,
  });

  const allLinksOpened = linksOpened.terms && linksOpened.privacy && linksOpened.refund;

  const handlePolicyLinkClick = (policyType) => {
    setLinksOpened((prev) => ({ ...prev, [policyType]: true }));
  };

  useEffect(() => {
    let profileInfo = profilePageInfo;
    if (!profileInfo) {
      const saved = localStorage.getItem("profilePageInfo");
      if (saved) profileInfo = JSON.parse(saved);
    }

    if (!profileInfo) return;

    const emailAuto = profileInfo?.emailApplicable ? profileInfo?.userName : "";
    const phoneAuto = profileInfo?.mobileApplicable
      ? profileInfo?.userName
      : "";

    setProfile({
      firstName: profileInfo?.firstName || "",
      lastName: profileInfo?.lastName || "",
      email: emailAuto,
      phone: phoneAuto,
    });
    setDisableEmail(!!emailAuto);
    setDisablePhone(!!phoneAuto);
  }, [profilePageInfo, isLoginModalOpen]);

  useEffect(() => {
    if (course) {
      sessionStorage.setItem("courseData", JSON.stringify(course));
    }
  }, [course]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const storedCourse = sessionStorage.getItem("courseData");
    const courseObj = storedCourse ? JSON.parse(storedCourse) : null;
    const courseUuid = courseObj?.courseUuid || courseObj?.uuid;

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(profile.phone)) {
      alert("Please enter a valid 10-digit phone number starting with 6-9");
      return;
    }

    const payload = {
      firstName: profile.firstName.trim(),
      lastName: profile.lastName.trim(),
      isdCode: "+91",
      mobile: profile.phone,
      email: profile.email,
      acceptConsent: acceptConsent,
    };

    try {
      setLoading(true);
      const res = await createProfile(payload);
      if (res?.errorCode === 0) {
        await getProfile();
        window.dispatchEvent(new Event("profileUpdated"));
        localStorage.setItem("personalInfoCompleted", "true");
        if (isEnroll && courseUuid) {
          const enrollRes = await enrollNow(courseUuid);
          if (enrollRes?.data?.errorCode === 0) {
            toast.success(
              enrollRes.data.message || "Course enrolled successfully"
            );
            navigate("/Dashboard");
            return;
          } else {
            toast.error(enrollRes?.data?.message || "Failed to enroll");
          }
        }
        navigate("/");
        setIsLoginModalOpen(false);
        toast.success(res?.message || "Profile updated successfully");
      } else {
        toast.error(res?.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        {/* Left Side - Profile Preview */}
        <div className="w-full lg:w-1/3 bg-gradient-to-b from-gray-50 to-white p-6 text-center border-b lg:border-b-0 lg:border-r border-gray-200">
          <div className="relative inline-block mb-4">
            <img
              src={UserProfile}
              alt="User"
              className="w-24 h-24 rounded-full mx-auto object-cover border-4 border-white shadow-lg"
            />
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mb-1">
            {profile.firstName || "Your"} {profile.lastName || "Name"}
          </h2>
          <p className="text-sm text-gray-500 mb-4">New Member</p>

          <ul className="text-sm text-gray-600 space-y-2">
            {profile.email && (
              <li className="flex items-center justify-center gap-2">
                <FaEnvelope className="text-gray-400" /> {profile.email}
              </li>
            )}
            {profile.phone && (
              <li className="flex items-center justify-center gap-2">
                <FaPhoneAlt className="text-gray-400" /> +91 {profile.phone}
              </li>
            )}
          </ul>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-2/3 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
            <p className="text-sm text-gray-500">
              Complete your profile to get started
            </p>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={50}
                name="firstName"
                value={profile.firstName}
                onChange={(e) => {
                  const val = e.target.value;
                  setProfile((prev) => ({ ...prev, firstName: val }));
                  if (!/^[A-Za-z]*$/.test(val)) {
                    e.target.setCustomValidity("Only alphabets are allowed");
                  } else {
                    e.target.setCustomValidity("");
                  }
                }}
                placeholder="First Name"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
                pattern="[A-Za-z]+"
                title="Only alphabets are allowed"
                required
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Last Name
              </label>
              <input
                type="text"
                maxLength={50}
                name="lastName"
                value={profile.lastName}
                onChange={(e) => {
                  const val = e.target.value;
                  setProfile((prev) => ({ ...prev, lastName: val }));
                  if (val && !/^[A-Za-z]*$/.test(val)) {
                    e.target.setCustomValidity("Only alphabets are allowed");
                  } else {
                    e.target.setCustomValidity("");
                  }
                }}
                placeholder="Last Name (optional)"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={profile.email}
                onChange={(e) => {
                  const val = e.target.value;
                  setProfile((prev) => ({ ...prev, email: val }));
                  if (val.trim() !== "") {
                    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                    if (!emailPattern.test(val)) {
                      e.target.setCustomValidity("Please enter a valid email");
                    } else {
                      e.target.setCustomValidity("");
                    }
                  } else {
                    e.target.setCustomValidity("");
                  }
                }}
                placeholder="Email (optional)"
                disabled={disableEmail}
                className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-gray-500 focus:outline-none ${
                  disableEmail ? "bg-gray-100 text-gray-500" : "bg-white"
                }`}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 text-sm text-gray-600 bg-gray-100 border border-r-0 border-gray-300 rounded-l">
                  +91
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={profile.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setProfile((prev) => ({ ...prev, phone: val }));
                  }}
                  placeholder="10-digit number"
                  disabled={disablePhone}
                  className={`w-full border border-gray-300 rounded-r px-3 py-2 text-sm focus:border-gray-500 focus:outline-none ${
                    disablePhone ? "bg-gray-100 text-gray-500" : "bg-white"
                  }`}
                  pattern="^[6-9]\d{9}$"
                  title="Enter a valid 10-digit mobile number"
                  maxLength={10}
                  required
                />
              </div>
            </div>

            {/* Consent Section - Full Width */}
            <div className="md:col-span-2 mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Please read our policies:
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                <a
                  href="/TermsConditionsPage"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handlePolicyLinkClick("terms")}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    linksOpened.terms
                      ? "bg-green-100 border-green-400 text-green-700"
                      : "bg-white border-gray-300 text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  {linksOpened.terms ? "✓ " : ""}Terms & Conditions
                </a>
                <a
                  href="/PrivacyPolicyPage"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handlePolicyLinkClick("privacy")}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    linksOpened.privacy
                      ? "bg-green-100 border-green-400 text-green-700"
                      : "bg-white border-gray-300 text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  {linksOpened.privacy ? "✓ " : ""}Privacy Policy
                </a>
                <a
                  href="/RefundPolicyPage"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handlePolicyLinkClick("refund")}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    linksOpened.refund
                      ? "bg-green-100 border-green-400 text-green-700"
                      : "bg-white border-gray-300 text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  {linksOpened.refund ? "✓ " : ""}Refund Policy
                </a>
              </div>

              <label className={`flex items-center gap-2 ${allLinksOpened ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                <input
                  type="checkbox"
                  checked={acceptConsent}
                  onChange={(e) => {
                    if (allLinksOpened) {
                      setAcceptConsent(e.target.checked);
                    }
                  }}
                  disabled={!allLinksOpened}
                  className="w-4 h-4 accent-gray-900"
                />
                <span className="text-sm text-gray-700">
                  I accept the Terms, Privacy Policy & Refund Policy
                </span>
              </label>
              {!allLinksOpened && (
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Please open all policy links above before accepting
                </p>
              )}
            </div>

            {/* Submit Button - Full Width */}
            <div className="md:col-span-2 mt-4">
              <button
                type="submit"
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-2.5 rounded transition flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading && (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {loading ? "Saving..." : "Save & Continue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoPage;
